import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { TableV2, TableV2Column } from 'components/Table/TableV2';
import { SearchInput } from 'components/Inputs/SearchInput';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getAtsLists } from '../api';
import { AtsList } from '../types';
import { formatDateFromNow, sortByDate } from 'utils/datetime';
import { useToast, ToastType } from 'services/toast';
import { Loading } from 'components/Loading';
import { LinkedCampaign } from './LinkedCampaign';
import { useConfirmationModal } from 'services/modals/hooks';

const headerContainer: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 2
};

type Props = {
  atsListId: string | undefined | null;
  setAtsListId: (atsListId: string | undefined) => void;
  onUploadCandidates: () => void;
  onTalkToCandidates: () => void;
  setCampaignToDisconnectFromAtsList: (campaignId: string | undefined) => void;
};

export const AtsLists: React.FC<Props> = ({
  setAtsListId,
  onUploadCandidates,
  onTalkToCandidates,
  atsListId,
  setCampaignToDisconnectFromAtsList
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [atsLists, setAtsLists] = useState<AtsList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { showModal } = useConfirmationModal();

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const response = await getAtsLists();
        setAtsLists(response.data);
      } catch (error) {
        showToast({
          message: 'Failed to fetch your lists',
          type: ToastType.Error
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetch();
  }, []);

  const columns: TableV2Column<AtsList>[] = [
    {
      name: 'List or group name',
      selector: (row) => row.name
    },
    {
      name: 'List ID',
      cell: (row) => row.id
    },
    {
      name: 'Candidates',
      selector: (row) => row.items.length
    },
    {
      name: 'Linked Popp campaign',
      cell: (row) => <LinkedCampaign campaignId={row.campaignId} />
    },
    {
      name: 'Last updated',
      selector: (row) => formatDateFromNow(row.updatedAt),
      sortable: true,
      sortFunction: (a, b) => sortByDate(a.updatedAt, b.updatedAt)
    }
  ];

  const filteredList = useMemo(() => {
    if (!searchValue) return atsLists;

    return atsLists.filter((list) =>
      list.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [atsLists.length, searchValue]);

  const onShowModelWhenModel = (row: AtsList) => {
    showModal({
      title: 'Link to this new campaign?',
      message:
        'Using this list for your new campaign will disconnect the existing one.',
      confirmationText: 'OK',
      cancelText: 'Cancel',
      onConfirm: () => {
        setAtsListId(row.id);
        setCampaignToDisconnectFromAtsList(row.campaignId);
      }
    });
  };

  const onSelectRow = (row: AtsList) => {
    if (!row) {
      setAtsListId(undefined);
      return;
    }

    if (row.campaignId) {
      onShowModelWhenModel(row);
      return;
    }

    setAtsListId(row.id);
    setCampaignToDisconnectFromAtsList(undefined);
  };

  return (
    <Box>
      <Box sx={headerContainer}>
        <Box>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onClear={() => setSearchValue('')}
            placeholder="Search"
          />
        </Box>

        <Stack direction="row" gap={3}>
          <Button
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={onUploadCandidates}
            variant="tertiary"
          >
            Upload candidates from spreadsheet
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              if (!atsListId) {
                showToast({
                  message: 'Please select an option.',
                  type: ToastType.Error
                });
                return;
              }
              onTalkToCandidates();
            }}
          >
            Confirm and continue
          </Button>
        </Stack>
      </Box>
      {isLoading ? (
        <Loading />
      ) : (
        <TableV2
          columns={columns}
          data={filteredList}
          onSelectedRowsChange={(selectedRows) => {
            onSelectRow(selectedRows.selectedRows[0]);
          }}
          selectableRowsSingle
          selectableRows
          pagination
        />
      )}
    </Box>
  );
};
